import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { Panel } from '../panels/Panel';
import { usePanelStore } from '../../store/panelStore';
import { useAnimationStore } from '../../store/animationStore';
import { AnimationToolbar } from './AnimationToolbar';
import { AnimationList } from './AnimationList';
import { AnimationProperties } from './AnimationProperties';
import { PlaybackControls } from './PlaybackControls';
import { TimelineRuler } from './TimelineRuler';
import { PropertyTrackList } from './PropertyTrackList';
import { DopeSheet } from './DopeSheet';
import { CurveEditor } from './CurveEditor';
import { AnimationPreview } from './AnimationPreview';
import { OnionSkinning } from './OnionSkinning';
import { cn } from '../../utils/cn';

const MIN_LIST_WIDTH = 150;
const MAX_LIST_WIDTH = 350;
const MIN_PREVIEW_WIDTH = 200;
const MAX_PREVIEW_WIDTH = 400;
const TRACK_HEADER_WIDTH = 180;

export const AnimationEditorPanel: React.FC = () => {
  const panel = usePanelStore((state) => state.panels['animation-editor']);

  const {
    selectedAnimationId,
    animations,
    viewMode,
    viewport,
    playback,
    showCurveEditor,
  } = useAnimationStore();

  const [animationListWidth, setAnimationListWidth] = useState(200);
  const [previewWidth, setPreviewWidth] = useState(250);
  const [isResizingLeft, setIsResizingLeft] = useState(false);
  const [isResizingRight, setIsResizingRight] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const [timelineWidth, setTimelineWidth] = useState(600);

  const animation = selectedAnimationId ? animations[selectedAnimationId] : null;
  const { pixelsPerSecond, scrollLeft } = viewport;
  const { currentTime } = playback;

  // Update timeline width when container resizes
  useEffect(() => {
    if (!timelineContainerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setTimelineWidth(Math.max(100, entry.contentRect.width - TRACK_HEADER_WIDTH));
      }
    });

    resizeObserver.observe(timelineContainerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Handle left resize (animation list)
  const handleLeftResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingLeft(true);

    const startX = e.clientX;
    const startWidth = animationListWidth;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const newWidth = Math.max(MIN_LIST_WIDTH, Math.min(MAX_LIST_WIDTH, startWidth + deltaX));
      setAnimationListWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizingLeft(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [animationListWidth]);

  // Handle right resize (preview panel)
  const handleRightResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingRight(true);

    const startX = e.clientX;
    const startWidth = previewWidth;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = startX - moveEvent.clientX;
      const newWidth = Math.max(MIN_PREVIEW_WIDTH, Math.min(MAX_PREVIEW_WIDTH, startWidth + deltaX));
      setPreviewWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizingRight(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [previewWidth]);

  // Determine which timeline view to show
  const renderTimelineContent = useMemo(() => {
    if (!animation) {
      return (
        <div className="flex items-center justify-center h-full text-zinc-500 text-sm">
          Select an animation to edit
        </div>
      );
    }

    if (viewMode === 'dopesheet') {
      return (
        <DopeSheet
          trackHeaderWidth={TRACK_HEADER_WIDTH}
          className="flex-1"
        />
      );
    }

    // Default timeline view
    return (
      <PropertyTrackList
        animationId={animation.id}
        trackHeaderWidth={TRACK_HEADER_WIDTH}
        timelineWidth={timelineWidth}
        pixelsPerSecond={pixelsPerSecond}
        scrollLeft={scrollLeft}
        currentTime={currentTime}
        duration={animation.duration}
      />
    );
  }, [animation, viewMode, timelineWidth, pixelsPerSecond, scrollLeft, currentTime]);

  if (!panel.visible) {
    return null;
  }

  return (
    <Panel panel={panel} className="flex flex-col h-full">
      {/* Toolbar */}
      <AnimationToolbar />

      {/* Main Content */}
      <div
        ref={containerRef}
        className="flex-1 flex min-h-0 overflow-hidden"
      >
        {/* Left Panel: Animation List + Properties */}
        <div
          className="flex flex-col flex-shrink-0 border-r border-zinc-700 overflow-hidden"
          style={{ width: animationListWidth }}
        >
          {/* Animation List */}
          <div className="flex-1 overflow-hidden min-h-0">
            <AnimationList className="h-full" />
          </div>

          {/* Animation Properties */}
          <div className="flex-shrink-0 border-t border-zinc-700 overflow-y-auto max-h-[40%]">
            <AnimationProperties />
          </div>
        </div>

        {/* Left Resize Handle */}
        <div
          className={cn(
            'w-1 flex-shrink-0 cursor-col-resize transition-colors',
            isResizingLeft ? 'bg-blue-500' : 'bg-zinc-800 hover:bg-blue-500'
          )}
          onMouseDown={handleLeftResizeStart}
        />

        {/* Center Panel: Timeline Area */}
        <div
          ref={timelineContainerRef}
          className="flex-1 flex flex-col min-w-0 overflow-hidden"
        >
          {/* Playback Controls */}
          <PlaybackControls />

          {/* Timeline Ruler */}
          <TimelineRuler width={timelineWidth + TRACK_HEADER_WIDTH} />

          {/* Timeline Content (PropertyTrackList or DopeSheet) */}
          <div className="flex-1 overflow-auto min-h-0">
            {renderTimelineContent}
          </div>

          {/* Curve Editor (shown at bottom when enabled or in curves mode) */}
          {(showCurveEditor || viewMode === 'curves') && (
            <CurveEditor />
          )}
        </div>

        {/* Right Resize Handle */}
        <div
          className={cn(
            'w-1 flex-shrink-0 cursor-col-resize transition-colors',
            isResizingRight ? 'bg-blue-500' : 'bg-zinc-800 hover:bg-blue-500'
          )}
          onMouseDown={handleRightResizeStart}
        />

        {/* Right Panel: Preview + Onion Skinning */}
        <div
          className="flex flex-col flex-shrink-0 border-l border-zinc-700 overflow-hidden"
          style={{ width: previewWidth }}
        >
          {/* Animation Preview */}
          <div className="flex-1 overflow-hidden min-h-0">
            <AnimationPreview className="h-full" />
          </div>

          {/* Onion Skinning Settings */}
          <OnionSkinning />
        </div>
      </div>
    </Panel>
  );
};

export default AnimationEditorPanel;
